import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp
} from 'firebase/firestore';
import { db, auth } from "../firebaseConfig";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData,
} from 'chart.js';
import { useAuth } from '.././AuthContext';
import { useAppSelector } from '../redux/store';
import getCurrentScreen from '../Helper/GetScreen';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface CallData {
    type: string;
    callTimestamp: Timestamp;
}

interface ChartDataType {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        borderColor: string;
        backgroundColor: string;
        fill: boolean;
        tension: number;
    }[];
}

interface UserOption {
    id: string;
    name: string;
}

const CallDistribution = () => {
    const { isAdmin, isManager, isStaff, userData, currentUser } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [chartData, setChartData] = useState<ChartDataType | null>(null);
    const [startTime, setStartTime] = useState<string>('09:00');
    const [endTime, setEndTime] = useState<string>('17:00');
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [teamSelectionMode, setTeamSelectionMode] = useState<'all' | "Managed" | 'individual' | ''>('');
    const [teamSelectionModeState, setTeamSelectionModeState] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const filterPopupRef = useRef<HTMLDivElement>(null);

    const timeLabels = Array.from({ length: 24 }, (_, i) => {
        const hour = i;
        if (hour === 0) return '12 AM';
        if (hour === 12) return '12 PM';
        return `${hour > 12 ? hour - 12 : hour}${hour >= 12 ? ' PM' : ' AM'}`;
    });

    // Handle outside clicks
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setFilterPopupOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Call Distribution (Filtered by Time)',
                font: {
                    size: 16,
                }
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Time',
                },
                grid: {
                    display: false,
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Number of Calls',
                },
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                }
            },
        },
    };

    // Fetch company ID and users
    useEffect(() => {
        const fetchCompanyId = async () => {
            const user = auth.currentUser;
            if (user) {
                const userDocRef = doc(db, "users", user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const companyId = userDocSnap.data().companyId;
                    setCompanyId(companyId);
                }
            }
        };
        fetchCompanyId();
    }, []);

    const fetchAssignedToOptions = useCallback(async () => {
        if (!companyId) return;
        try {
            let usersQuery = query(
                collection(db, "users"),
                where("companyId", "==", companyId)
            );

            if (isManager && currentScreen === 'Manager') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
            } else if (isStaff && currentScreen === 'Staff') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("userId", "==", currentUser?.uid)
                );
            }

            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name
            }));
            setUsers(users);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [companyId]);

    useEffect(() => {
        fetchAssignedToOptions();
    }, [fetchAssignedToOptions]);

    const initialFilterState = async () => {
        if (isAdmin && currentScreen === 'Admin') {
            setTeamSelectionMode('all');

        } else if (isManager && currentScreen === 'Manager') {
            setTeamSelectionMode('Managed');

        } else if (isStaff && currentScreen === 'Staff') {
            setTeamSelectionMode('individual');
            setSelectedUserId(currentUser?.uid ? currentUser.uid : null);
        }
    };

    useEffect(() => {
        initialFilterState();
    }, [isAdmin, isManager, isStaff, currentScreen]);

    // Fetch call data (rest of the fetch call data useEffect remains the same)
    useEffect(() => {
        const fetchCallData = async () => {
            if (!companyId) return;

            try {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const start = new Date(today);
                const [startHour, startMinute] = startTime.split(':').map(Number);
                start.setHours(startHour, startMinute, 0, 0);
                const end = new Date(today);
                const [endHour, endMinute] = endTime.split(':').map(Number);
                end.setHours(endHour, endMinute, 0, 0);
                const startTimestamp = Timestamp.fromDate(start);
                const endTimestamp = Timestamp.fromDate(end);

                let callLogsRef = collection(db, `companies/${companyId}/callLogs`);
                let timeQuery = query(
                    callLogsRef,
                    where('callTimestamp', '>=', startTimestamp),
                    where('callTimestamp', '<', endTimestamp)
                );

                if (teamSelectionMode === "Managed") {
                    // find all users that have the same managerId as the current user
                    const usersQuery = query(
                        collection(db, 'users'),
                        where('companyId', '==', userData?.companyId),
                        where('managerId', '==', currentUser?.uid)
                    );
                    const usersSnapshot = await getDocs(usersQuery);
                    const users = usersSnapshot.docs.map(doc => doc.id);
                    timeQuery = query(
                        callLogsRef,
                        where('callTimestamp', '>=', startTimestamp),
                        where('callTimestamp', '<', endTimestamp),
                        where('userId', 'in', users)
                    );
                } else if (teamSelectionMode === 'individual' && selectedUserId) {
                    timeQuery = query(
                        callLogsRef,
                        where('callTimestamp', '>=', startTimestamp),
                        where('callTimestamp', '<', endTimestamp),
                        where('userId', '==', selectedUserId)
                    );
                }

                const querySnapshot = await getDocs(timeQuery);
                const calls = querySnapshot.docs.map(doc => ({
                    ...doc.data(),
                    callTimestamp: doc.data().callTimestamp.toDate()
                } as unknown as { callTimestamp: Date }));

                const hourlyCalls = new Array(24).fill(0);

                calls.forEach(call => {
                    const hour = call.callTimestamp.getHours();
                    hourlyCalls[hour]++;
                });

                const startHourIndex = start.getHours();
                const endHourIndex = end.getHours();
                const filteredHourlyCalls = hourlyCalls.slice(startHourIndex, endHourIndex + 1);
                const filteredTimeLabels = timeLabels.slice(startHourIndex, endHourIndex + 1);

                const newChartData: ChartDataType = {
                    labels: filteredTimeLabels,
                    datasets: [
                        {
                            label: 'Total Calls',
                            data: filteredHourlyCalls,
                            borderColor: '#3483BE',
                            backgroundColor: 'rgba(52, 131, 190, 0.1)',
                            fill: true,
                            tension: 0.4
                        }
                    ],
                };

                setChartData(newChartData);
            } catch (error) {
                console.error("Error fetching call logs:", error);
            }
        };

        fetchCallData();
    }, [companyId, startTime, endTime,  selectedUserId]);

    const handleAllIndividuals = () => {
        if (isAdmin && currentScreen === 'Admin') {
            setTeamSelectionMode('all');
            setTeamSelectionModeState(false);
            setSelectedUserId(null);
            setSearchQuery('');
        } else if (isManager && currentScreen === 'Manager') {
            setTeamSelectionMode('Managed');
            setTeamSelectionModeState(false);
            setSelectedUserId(null);
            setSearchQuery('');
        }
    }

    const handleIndividual = () => {
        setTeamSelectionMode('');
        setTeamSelectionModeState(true);
    }

    const handleUserSelection = (userId: string) => {
        setTeamSelectionMode('individual');
        setSelectedUserId(userId);
        const selectedUser = users.find(user => user.id === userId);
        if (selectedUser) {
            setSearchQuery(selectedUser.name);
        }
        // setTeamSelectionModeState(false);
        setShowDropdown(false);
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div>
            <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div
                        className="flex items-center justify-center w-16 h-16 rounded-full"
                        style={{
                            background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)",
                        }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/phone-calling-rounded.svg`}
                            alt="Icon"
                            className="w-10 h-10"
                        />
                    </div>
                    <h2 className="text-2xl text-black font-medium"
                        style={{
                            fontFamily: "Montserrat",
                            lineHeight: "29.26px",
                        }}>
                        Call Distribution
                        <span style={{
                            maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle',
                        }}>
                            {selectedUserId && `- ${searchQuery}`}
                        </span>
                    </h2>
                </div>
                {currentScreen !== 'Staff' && (
                    <div>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                            alt="Settings Icon"
                            className="w-8 h-8 cursor-pointer"
                            onClick={() => setFilterPopupOpen(!filterPopupOpen)}
                        />
                    </div>
                )}
            </div>
            <div className="h-[300px]">
                {chartData && <Line options={options} data={chartData} />}
            </div>
            <div className="flex gap-4">
                <div>
                    <label className="text-xs w-full mb-1 mr-2">Start Time:</label>
                    <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="p-1 border border-gray-300 rounded-2xl text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs w-full mb-1 mr-2">End Time:</label>
                    <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="p-1 border border-gray-300 rounded-2xl text-sm"
                    />
                </div>
            </div>

            {filterPopupOpen && (
                <div ref={filterPopupRef} className="absolute top-16 right-4 w-[261px] bg-white border rounded-3xl shadow-lg p-4 z-50">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-[14px] font-normal leading-[20px]">Team Selection</h3>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                            alt="filters-black-icon"
                            className="w-9 h-9"
                        />
                    </div>
                    <div className="mb-4">
                        <div className="mb-2">
                            <label className="block mb-2 cursor-pointer">
                                <input
                                    type="radio"
                                    className="mr-2"
                                    checked={isAdmin && currentScreen === "Admin" && teamSelectionMode === 'all' || isManager && currentScreen === "Manager" && teamSelectionMode === 'Managed'}
                                    onChange={() => {
                                        handleAllIndividuals();
                                    }}
                                />
                                All Team
                            </label>
                            <label className="block cursor-pointer">
                                <input
                                    type="radio"
                                    className="mr-2"
                                    checked={teamSelectionModeState === true}
                                    onChange={() => handleIndividual()}
                                />
                                Individual Member
                            </label>
                        </div>

                        {teamSelectionModeState === true && (
                            <div className="mt-4" ref={dropdownRef}>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search user..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onClick={() => setShowDropdown(true)}
                                        className="w-full p-2 border rounded-md"
                                    />
                                    {showDropdown && (
                                        <ul className="absolute w-full bg-white border rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto z-50">
                                            {filteredUsers.map((user) => (
                                                <li
                                                    key={user.id}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => handleUserSelection(user.id)}
                                                >
                                                    {user.name}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallDistribution;